import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Download, Send } from "lucide-react";
import { api, errMsg, REGIMI } from "@/lib/api";
import { Field } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const MODULO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/modulo-attivazione.pdf`;
const TOP = ["denominazione", "piva", "codice_fiscale", "regime_fiscale", "indirizzo", "cap", "comune", "provincia", "pec", "codice_destinatario", "email", "telefono"];
const UP = ["piva", "codice_fiscale", "provincia", "codice_destinatario", "lr_cf", "lr_nascita_provincia", "lr_res_provincia"];
// [key, label, required, maxLength, wide, type]
const ANAG = [["denominazione", "Ragione sociale / Nome e cognome", true, 150, true], ["nazione", "Nazione", true, 40], ["piva", "Partita IVA", true, 16],
  ["codice_fiscale", "Codice fiscale", true, 16], ["comune", "Con sede in (città)", true, 80], ["provincia", "Provincia (sigla)", true, 2], ["cap", "CAP", true, 5],
  ["indirizzo", "Indirizzo", true, 150], ["civico", "N° civico", true, 10], ["tipo_attivita", "Tipo di attività", true, 150, true],
  ["codice_destinatario", "Codice destinatario", false, 7], ["pec", "Indirizzo PEC", false, 120], ["telefono", "Telefono", true, 30], ["fax", "Fax", false, 30],
  ["email", "Indirizzo email", true, 120, false, "email"], ["codice_ipa", "Codice IPA (solo PA)", false, 20],
  ["esercizio_da", "Esercizio fiscale dal (GG/MM)", false, 5], ["esercizio_a", "Esercizio fiscale al (GG/MM)", false, 5]];
const LEGALE = [["lr_nome", "Nome", true, 60], ["lr_cognome", "Cognome", true, 60], ["lr_cf", "Codice fiscale", true, 16], ["lr_nascita_data", "Data di nascita", true, 10, false, "date"],
  ["lr_nascita_comune", "Città di nascita", true, 80], ["lr_nascita_provincia", "Provincia", false, 2], ["lr_nascita_cap", "CAP", false, 5], ["lr_nascita_nazione", "Nazione", false, 40],
  ["lr_res_comune", "Città di residenza", true, 80], ["lr_res_provincia", "Provincia", false, 2], ["lr_res_cap", "CAP", false, 5], ["lr_res_indirizzo", "Indirizzo", true, 150],
  ["lr_res_civico", "N° civico", false, 10], ["lr_res_nazione", "Nazione", false, 40], ["lr_cellulare", "Cellulare", true, 30], ["lr_email", "Mail", true, 120, false, "email"]];
const INIT = { soggetto: "Azienda", regime_fiscale: "RF01", nazione: "Italia", lr_nascita_nazione: "Italia", lr_res_nazione: "Italia", esercizio_da: "01/01", esercizio_a: "31/12" };

const Fields = ({ list, f, set }) => list.map(([k, l, req, max, wide, type]) => (
  <Field key={k} label={`${l}${req ? " *" : ""}`} className={wide ? "sm:col-span-2" : ""}>
    <Input data-testid={`attiv-${k}-input`} type={type || "text"} required={req} maxLength={max} value={f[k] || ""}
      onChange={(e) => set(k, UP.includes(k) ? e.target.value.toUpperCase() : e.target.value)} />
  </Field>
));

const Pick = ({ label, value, onChange, opts, testid }) => (
  <Field label={label}>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
    </Select>
  </Field>
);

const Title = ({ children }) => <h3 className="mt-2 rounded-md bg-slate-900 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white sm:col-span-2">{children}</h3>;

const Consent = ({ testid, checked, onChange, children }) => (
  <label className="flex items-start gap-3 text-sm text-slate-600">
    <Checkbox data-testid={testid} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
    <span>{children}</span>
  </label>
);

export const AttivazioneForm = ({ pkg }) => {
  const [f, setF] = useState(INIT);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  useEffect(() => {
    if (done) window.scrollTo({ top: 0 });
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.privacy || !f.termini) return toast.error("Accetta l'informativa privacy e le condizioni del servizio");
    const body = { tipo: "azienda", package_id: pkg.id, privacy: true, termini: true, nome: `${f.lr_nome} ${f.lr_cognome}`.trim(), modulo: {} };
    Object.entries(f).forEach(([k, v]) => {
      if (TOP.includes(k)) body[k] = v;
      else if (!["privacy", "termini"].includes(k) && v) body.modulo[k] = v;
    });
    setBusy(true);
    try {
      await api.post("/account-requests", body);
      setDone(true);
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div data-testid="attiv-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
        <h3 className="mt-4 font-display text-xl font-bold text-slate-900">Richiesta di attivazione inviata</h3>
        <p className="mt-2 text-sm text-slate-600">Abbiamo ricevuto il modulo compilato. Ti ricontatteremo all'email indicata per attivare il servizio.</p>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="attiv-form" className="space-y-5">
      <div data-testid="request-package-name" className="rounded-lg bg-sky-50 px-4 py-3 text-sm text-sky-900">Pacchetto scelto: <b>{pkg.name}</b></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Title>Dati anagrafici</Title>
        <Pick label="Azienda / Libero professionista *" testid="attiv-soggetto-select" value={f.soggetto} onChange={(v) => set("soggetto", v)}
          opts={[["Azienda", "Azienda"], ["Libero professionista", "Libero professionista"]]} />
        <Pick label="Regime fiscale *" testid="attiv-regime-select" value={f.regime_fiscale} onChange={(v) => set("regime_fiscale", v)} opts={Object.entries(REGIMI)} />
        <Fields list={ANAG} f={f} set={set} />
        <Title>Legale rappresentante</Title>
        <Fields list={LEGALE} f={f} set={set} />
      </div>
      <div className="space-y-3 rounded-lg border border-slate-200 p-4">
        <Consent testid="attiv-privacy-checkbox" checked={!!f.privacy} onChange={(v) => set("privacy", v)}>
          Ai sensi dell'art. 13 del Regolamento europeo 679/16 (GDPR) dichiaro di aver letto ed accettato l'<Link to="/privacy" target="_blank" className="text-sky-700 underline">informativa sulla privacy</Link>. *
        </Consent>
        <Consent testid="attiv-termini-checkbox" checked={!!f.termini} onChange={(v) => set("termini", v)}>
          Ho preso visione e confermo l'accettazione delle <Link to="/termini" target="_blank" className="text-sky-700 underline">condizioni generali del servizio</Link> di fatturazione elettronica P.A., B2B e B2C. *
        </Consent>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <a href={MODULO_URL} data-testid="attiv-blank-pdf-link" className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:underline">
          <Download className="h-4 w-4" />Preferisci compilarlo a mano? Scarica il modulo PDF
        </a>
        <Button data-testid="attiv-submit-button" disabled={busy} className="h-11 bg-blue-600 px-8 hover:bg-blue-700">
          <Send className="mr-2 h-4 w-4" />{busy ? "Invio in corso…" : "Invia richiesta di attivazione"}
        </Button>
      </div>
    </form>
  );
};
